import { useResetPasswordMutation } from 'util/rtk-api'
import { useDocTitle } from 'util/hooks'

import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { showToast } from 'components/toast'
import { showMutationError } from 'util/mutation-error'
import { AuthCard, AuthDivider } from 'auth/auth-card'

export const ResetPassword = () => {
	const searchParams = new URLSearchParams(window.location.search)
	const email = searchParams.get('email')
	const from = searchParams.get('from')
	const navigate = useNavigate()
	const [ resetPassword ] = useResetPasswordMutation()

	const [ code, setCode ] = useState('')
	const [ password, setPassword ] = useState('')

	const checkCode = target => {
		if (
			target.value.match(/\d{6}/)
		) {
			target.setCustomValidity('')
			return
		}

		target.setCustomValidity(
			'The pin should be a 6-digit number.'
		)
	}

	const onPasswordBlur = ({ target }) => {
		const conditions = [
			/.{8,}/,
			/[0-9]+/,
			/[a-z]+/,
			/[A-Z]+/,
			/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]+/,
		]

		setPassword(target.value)

		if (
			conditions.every(pattern => target.value.match(pattern))
		) {
			target.setCustomValidity('')
			return
		}
		
		target.setCustomValidity(
			'The password must be at least 8 characters long and must use a lower case character, an upper case character, a number, and a special character.'
		)
	}

	// Checked as it is typed, not only on blur: a mismatch could otherwise be submitted with Enter
	// straight after typing the confirmation.
	const onConfirmPasswordChange = ({ target }) => {
		if (target.value === password) {
			target.setCustomValidity('')
			return
		}

		target.setCustomValidity(
			'This does not match the password entered above'
		)
	}

	const onFormSubmit = async event => {
		event.preventDefault()

		const result = await resetPassword({ confirmationCode: code, username: email, password })

		if (showMutationError(result)) return

		showToast('Password reset successfully', 'success')
		navigate(`/success?from=${from || ''}`)
	}

	useDocTitle('All for One - Reset Password')

	return (
		<AuthCard
			eyebrow={from === 'settings' ? 'Your account' : 'Check your email'}
			title="Reset password"
			subtitle={<>You’ll get an email at {email} if it’s linked to your account.</>}
		>
			<form
				id="reset-password-form"
				action="#"
				method="POST"
				onSubmit={onFormSubmit}
			>
				<div className="auth-field">
					<label htmlFor="code" className="auth-field-label">Verification code</label>
					<input
						type="tel"
						id="code"
						name="code"
						placeholder="6-digit code"
						required
						value={code}
						onChange={({ target }) => { setCode(target.value); checkCode(target) }}
					/>
				</div>

				<input className="auth-hidden-input"></input>

				<div className="auth-field">
					<label htmlFor="new-password" className="auth-field-label">New password</label>
					<input
						type="password"
						id="new-password"
						name="new-password"
						placeholder="New password"
						required
						onChange={({ target }) => setPassword(target.value)}
						onBlur={onPasswordBlur}
					/>
				</div>

				<div className="auth-field">
					<label htmlFor="confirm-password" className="auth-field-label">Confirm new password</label>
					<input
						type="password"
						id="confirm-password"
						name="confirm-password"
						placeholder="Confirm new password"
						required
						onChange={onConfirmPasswordChange}
					/>
				</div>

				<button type="submit" className="auth-btn">Reset password</button>
			</form>

			<AuthDivider />

			<p className="auth-prompt">
				{from === 'settings' ? 'Changed your mind?' : 'Remembered your password?'}{' '}
				<Link
					to={from === 'settings' ? '/settings' : '/login'}
					className="auth-link"
				>{from === 'settings' ? 'Back to settings' : 'Log in'}</Link>
			</p>
		</AuthCard>
	)
}
